import React from 'react'
// @src imports
import { Address } from 'src/chrome'
import { AddressForm } from 'src/contacts/components'
import { useEffect, useSelector, useState } from 'src/hooks'
import styles from './returnAddress.module.scss'
import { OrderWithPartialLines } from 'src/redux/reducers/orders'
import {
  Checkbox,
  Div,
  Flex,
  Text,
  Tooltip,
  Transition,
} from '@sendoutcards/quantum-design-ui'
import { emptyShippingAddress } from 'src/app/constants'
import { Button as DSButton } from 'src/design_system/molecules/button/Button'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'
import { AddressInput } from 'src/graphql/generated/graphql'
import omit from 'lodash/omit'
import HoverTooltip from 'src/chrome/HoverTooltip/HoverTooltip'

interface ReturnAddressProps {
  showRequired: boolean
  onSave: (address: AddressInput, shouldUpdateAccount: boolean) => void
  order: OrderWithPartialLines
  toggleAddressBlinker: (toggle: boolean) => void
  id?: string
  heading: { title: string; subtitle?: string }
}

const ReturnAddress: React.FC<ReturnAddressProps> = props => {
  const {
    showRequired,
    onSave,
    order,
    toggleAddressBlinker,
    heading = { title: 'Return Address' },
  } = props
  const { isSaving } = useSelector(state => state.orders)
  const { isMobile } = useSelector(state => state.window)
  const [shouldUpdateAccount, setShouldUpdateAccount] = useState(false)

  const [isEditing, setIsEditing] = useState(
    !order.returnAddress || !order.returnAddress.address1,
  )

  const [address, setAddress] = useState(
    omit(order.returnAddress, '__typename') || emptyShippingAddress,
  )

  const [hasInitializedSave, setHasInitializedSave] = useState(false)

  useEffect(() => {
    setAddress(omit(order.returnAddress, '__typename') || emptyShippingAddress)
  }, [order.returnAddress])

  const handleChange: React.ReactEventHandler<HTMLInputElement> = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setAddress({
      ...address,
      [event.target.name]: event.target.value,
    })
  }

  const handleCountryChange = (country: string) =>
    setAddress({
      ...address,
      country: country,
    })

  const handleStateChange = (state: string) =>
    setAddress({
      ...address,
      state: state,
    })

  const handleSave = () => {
    onSave(address, shouldUpdateAccount)
    setIsEditing(false)
    setHasInitializedSave(true)
  }

  useEffect(() => {
    if (!isSaving && hasInitializedSave) {
      setTimeout(() => {
        setHasInitializedSave(false)
      }, 2300)
    }
  }, [hasInitializedSave, isSaving])

  return (
    <>
      <Flex borderRadius="large" width="100%" id={props.id}>
        {hasInitializedSave ? (
          <Transition
            loadedStatus={'success'}
            title={
              isSaving ? 'Saving your address...' : 'Address saved successfully'
            }
            isLoading={isSaving}
          />
        ) : (
          <div className={styles.container}>
            <div className={styles.header}>
              <Div inset={{ bottom: 'x1' }}>
                <Text
                  type="largeBody"
                  weight="semiBold"
                  color="primaryHeading"
                  content={heading.title}
                  style={{ fontSize: '1.25rem', fontWeight: 600 }}
                  whiteSpace="nowrap"
                />
                <Text
                  type="body"
                  content={heading.subtitle}
                  whiteSpace="normal"
                  outset={{ right: 'x1' }}
                />
              </Div>
              {showRequired && (
                <Div display="flex" position="absolute" top="8px">
                  <Tooltip
                    background="dark"
                    direction="bottom"
                    title="Please make sure you have a return address"
                  />
                </Div>
              )}
              {!order.isSent && (
                <Div
                  display="flex"
                  width="100%"
                  justifyContent="flex-end"
                  style={{ gap: '16px' }}
                >
                  {isEditing && (
                    <HoverTooltip
                      positioning={{ top: '-36px' }}
                      title="Check to update the return address on your account"
                    >
                      <Checkbox
                        label="Save as Default"
                        onChange={setShouldUpdateAccount}
                        isChecked={shouldUpdateAccount}
                      />
                    </HoverTooltip>
                  )}
                  <DSButton
                    id={'edit_or_cancel_return_address_btn'}
                    title={
                      !isMobile
                        ? {
                            content: isEditing
                              ? 'Cancel'
                              : 'Use Different Address',
                            fontSize: '12px',
                          }
                        : undefined
                    }
                    onClick={() => {
                      setIsEditing(e => !e)
                    }}
                    fill="#f5f6f7"
                    padding="10px 16px"
                    borderRadius="small"
                    gap="x_5"
                  >
                    <Icon
                      name={isEditing ? 'cancel' : 'edit'}
                      size={isEditing ? 12 : 17}
                      color="#000"
                    />
                  </DSButton>
                </Div>
              )}
            </div>
            {isEditing ? (
              <AddressForm
                toggleAddressBlinker={toggleAddressBlinker}
                contact={address}
                onChange={handleChange}
                onCountryChange={handleCountryChange}
                onStateChange={handleStateChange}
                shouldShowDates={false}
                isTitleVisible={false}
                onSubmit={handleSave}
                submitButtonStyles={{ alignAction: 'flex-end' }}
              />
            ) : (
              <Address address={address} />
            )}
          </div>
        )}
      </Flex>
    </>
  )
}

export default ReturnAddress
